import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { Button, Typography, Chip, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import ErrorIcon from '@material-ui/icons/Error';

// CONSTANTS
import { ROUTE_DISCOVER_APP } from '@/constants/routes';
import { appsListStatuses } from '@/constants/app';

// UTILITY
import { getLink } from '@/utility/routes';
import { formatPrice } from '@/utility/currency';
import { popupCenter, updateUrl } from '@/utility/common';
import { externalLink, getExternalUrlParams } from '@/utility/analytics';
import { getSubscriptionLink } from '@/utility/subscription';
import { formatDate } from '@/utility/date';

// COMPONENTS
import RouterLink from '@/components/Link';

// HELP
import SeasonsInfo from './SeasonsInfo';

// STYLES
import { Container, LinkWrapper } from './styles';

const ServiceCard = ({
  isAllSeasons,
  data,
  analyticsData,
  partner,
  unique_id,
  anonymousId,
  fullHeight,
  location,
  onWatchNowClick,
}) => {
  const [t] = useTranslation();
  const appLink = useMemo(() => getLink(ROUTE_DISCOVER_APP, { slug: data.unique_slug }), [
    data.unique_slug,
  ]);

  const externalUrlParams = getExternalUrlParams({ unique_id: unique_id || anonymousId });
  let href = updateUrl(data.content_url || data.url, externalUrlParams);

  if (data.product_plan) {
    const productPlan = data.product_plan || {};

    href = getSubscriptionLink(
      data.unique_slug,
      productPlan.id,
      (data.addon_plan && [data.addon_plan]) || [],
    );
  }

  function getPrice(price, translation = 'common.costMonthWithTag') {
    return price && Math.ceil(price) !== 0 ? (
      <Trans i18nKey={translation} values={{ cost: formatPrice(price) }} />
    ) : (
      t('common.free')
    );
  }

  function getPricesContent() {
    const { price, deal_price, prices, source_type, list_status, plan, extra_content_price } = data;

    if (list_status === appsListStatuses.have) {
      return (
        <Typography className="p4" align="center">
          {t('common.sourceTypes.youHaveIt')}
        </Typography>
      );
    }

    const hasPlan = !!plan;
    const planPrice = plan?.price;
    const servicePrice = hasPlan && !!planPrice ? planPrice : price;
    const serviceDealPrice = hasPlan && !!planPrice ? plan.deal_price : deal_price;

    const priceContentFreePaid =
      serviceDealPrice || serviceDealPrice === 0 ? (
        <div className="service-card__deal-price">
          <Typography className="service-card__old-cost p5" color="textSecondary">
            {getPrice(servicePrice)}
          </Typography>
          <Typography className="service-card__cost" color="textSecondary">
            {getPrice(serviceDealPrice)}
          </Typography>
        </div>
      ) : (
        <Typography className="service-card__cost" color="textSecondary" align="center">
          {getPrice(servicePrice)}
        </Typography>
      );

    let priceContent = null;
    const title = [];

    if (source_type) {
      const rentalCost =
        prices && prices.rent && prices.rent.reduce((acc, item) => (acc = item.price), 0);
      const purchaseCost =
        prices && prices.buy && prices.buy.reduce((acc, item) => (acc = item.price), 0);
      const hasAnyCost = rentalCost || purchaseCost;

      // type is free
      // or
      // subscription
      if (source_type.type === 0 || source_type.type === 2) {
        title.push(t('common.sourceTypes.subscription'));
        priceContent = priceContentFreePaid;
      }

      // type is store
      if (source_type.type === 3) {
        if (hasAnyCost) {
          const cost = [];

          if (rentalCost) {
            title.push(t('common.sourceTypes.rent'));
            cost.push(`$${rentalCost}`);
          }

          if (purchaseCost) {
            title.push(t('common.sourceTypes.buy'));
            cost.push(`$${purchaseCost}`);
          }

          priceContent = (
            <Typography variant="body2" align="center" color="textSecondary">
              {cost.join('/')}
            </Typography>
          );
        } else {
          title.push(t('common.sourceTypes.rent'));
          title.push(t('common.sourceTypes.buy'));

          priceContent = true;
        }
      }

      // fallback
      if (!priceContent) {
        title.push('-');
        priceContent = priceContentFreePaid;
      }
    } else {
      title.push(t('common.sourceTypes.subscription'));
      priceContent = priceContentFreePaid;
    }

    const additionalPrice = extra_content_price && (
      <Tooltip
        title={extra_content_price.description}
        placement="top"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Typography
          className="service-card__additional-price p4"
          align="center"
          color="textSecondary"
        >
          {getPrice(extra_content_price.price, 'common.costAdditional')}{' '}
          <ErrorIcon
            className="service-card__additional-price-icon"
            color="inherit"
            fontSize="inherit"
          />
        </Typography>
      </Tooltip>
    );

    return (
      <>
        <Typography className="p4" align="center">
          {title.join('/')}
        </Typography>
        {priceContent}
        {additionalPrice}
      </>
    );
  }

  function handleExternalLink() {
    const properties = {
      ...analyticsData,
      location,
      href,
      app_id: data.id,
      service_name: data.name,
      is_affiliate: !!data.is_affiliate,
      partner: partner.partner_slug || 0,
      type: data.content_url ? 'content link' : 'app link',
    };

    externalLink(properties);
  }

  function renderBtn() {
    const { url, content_url, external_button_text, product_plan, arriving_date } = data;

    let onClick;
    let text;

    if (arriving_date) {
      text = t('common.comingSoon');
    } else if (content_url) {
      text = t('buttons.watchNow');
      onClick = e => {
        e.preventDefault();
        e.stopPropagation();
        popupCenter(href, window.innerWidth * 0.8, window.innerHeight * 0.8);
        handleExternalLink();
        onWatchNowClick();
      };
    } else if (product_plan) {
      text = external_button_text ?? t('buttons.subscribe');
      onClick = handleExternalLink;
    } else if (url) {
      text = external_button_text ?? t('buttons.visitSite');
      onClick = handleExternalLink;
    }

    return (
      <Button
        className="service-card__button"
        color="primary"
        size="medium"
        target="_blank"
        href={href}
        onClick={onClick}
        fullWidth
        disabled={!!arriving_date}
      >
        {text}
      </Button>
    );
  }

  const planLabel = data.plan?.label_title;
  const addon = data.addon_plan;
  const arrivingDate = data.arriving_date && formatDate(data.arriving_date, 'MMM d yyyy');

  return (
    <Container className={clsx('service-card', fullHeight && 'service-card--full-height')}>
      {planLabel && !arrivingDate && (
        <Chip label={planLabel} color="secondary" className="service-card__plan-chip" />
      )}
      {arrivingDate && (
        <Chip
          className="service-card__arr-date-chip"
          label={`${t('common.available')} ${arrivingDate}`}
          color="secondary"
        />
      )}
      {!!addon && (
        <Tooltip key={`addon-${addon.plan_id}`} title={addon.name}>
          <div className="service-card__addon-item">
            <img
              src={addon.img || `https://dummyimage.com/24X24.png?text=${addon.name}`}
              alt={addon.name}
            />
          </div>
        </Tooltip>
      )}
      <LinkWrapper>
        <RouterLink to={appLink} location={location}>
          <div className="service-card__logo">
            <img
              src={
                data.thumbnail || `https://dummyimage.com/200X80/FFFFFF/000000/?text=${data.name}`
              }
              alt={data.name}
            />
          </div>
          <div className="service-card__main-info">
            <div className="price__content">{getPricesContent()}</div>
            {isAllSeasons && <SeasonsInfo data={data} />}
          </div>
        </RouterLink>
      </LinkWrapper>
      {renderBtn()}
    </Container>
  );
};

ServiceCard.defaultProps = {
  data: {},
  analyticsData: {},
  partner: {},
  isAuth: false,
  isAllSeasons: false,
  fullHeight: false,
  location: '',
  onWatchNowClick: () => {},
};

ServiceCard.propTypes = {
  isAllSeasons: PropTypes.bool,
  data: PropTypes.object,
  analyticsData: PropTypes.object,
  type: PropTypes.string.isRequired, // movie, show
  partner: PropTypes.object,
  isAuth: PropTypes.bool,
  unique_id: PropTypes.string.isRequired,
  anonymousId: PropTypes.string.isRequired,
  fullHeight: PropTypes.bool,
  location: PropTypes.string,
  onWatchNowClick: PropTypes.func,
};

export default React.memo(ServiceCard);
